/**
 * Tournament Maker Component
 *
 *  토너먼트를 생성하여 사용할 수 있는 화면
 * - 단계별 진행: 1) 엘리미네이션 타입 선택 -> 2) 플레이어 입력 -> 3) 토너먼트 생성
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiSearch, ApiSave} from '@assets/js/api/commonApi.js';
import '@assets/css/comp-form-trmntBracket.css';
import '@assets/css/competition.css';

const TrmntMaker = (props) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // SessionStorage 키
    const STORAGE_KEY = 'trmnt-maker-data';

    // URL 파라미터에서 mtmkgCd와 armType 가져오기
    const mngCdFromUrl = searchParams.get('mngCd');
    const mtmkgCdFromUrl = searchParams.get('mtmkgCd');
    const armTypeFromUrl = searchParams.get('armType');
    const isFromHome = !!(mngCdFromUrl && mtmkgCdFromUrl && armTypeFromUrl);

    /**********************
     * SessionStorage 초기 데이터 로드
    ***********************/
    const loadInitialState = () => {
        const savedData = sessionStorage.getItem(STORAGE_KEY);
        if (savedData) {
            try {
                return JSON.parse(savedData);
            } catch (e) {
                console.error('Failed to load tournament data from sessionStorage:', e);
                return null;
            }
        }
        return null;
    };

    const initialData = loadInitialState();

    // State 초기화 (sessionStorage 데이터가 있으면 해당 데이터로, 없으면 기본값으로)
    const [step, setStep] = useState(initialData?.step || 1);
    const [eliminationType, setEliminationType] = useState(initialData?.eliminationType || '');
    const [playerCount, setPlayerCount] = useState(initialData?.playerCount || 0);
    const [players, setPlayers] = useState(initialData?.players || []);
    const [tournamentData, setTournamentData] = useState(initialData?.tournamentData || []);
    const [isRankingVisible, setIsRankingVisible] = useState(initialData?.isRankingVisible !== undefined ? initialData.isRankingVisible : true);
    const [isRankingExpanded, setIsRankingExpanded] = useState(initialData?.isRankingExpanded !== undefined ? initialData.isRankingExpanded : true);
    const [showGuideModal, setShowGuideModal] = useState(false);
    const [theme, setTheme] = useState(initialData?.theme || 'light'); // 'light' | 'dark'

    // Home에서 온 경우 대회 정보 상태
    const [competitionInfo, setCompetitionInfo] = useState(null);

    /**********************
     * SessionStorage 자동 저장
    ***********************/

    // State 변경 시 SessionStorage에 자동 저장
    useEffect(() => {
        const dataToSave = {
            step,
            eliminationType,
            playerCount,
            players,
            tournamentData,
            isRankingVisible,
            isRankingExpanded,
            theme
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, [step, eliminationType, playerCount, players, tournamentData, isRankingVisible, isRankingExpanded, theme]);

    /**********************
     * Step 1: 엘리미네이션 타입 선택
    ***********************/
    const handleTypeSelect = (type) => {
        // 토너먼트 타입 변경 시 모든 데이터 리셋
        setPlayerCount(0);
        setPlayers([]);
        setTournamentData([]);
        setEliminationType(type);
        setStep(2);
    };

    /**********************
     * Step 2: 플레이어 수 선택 및 입력
    ***********************/
    const handlePlayerCountSelect = (count) => {
        // 인원수 변경 시 플레이어 및 토너먼트 데이터 리셋
        setTournamentData([]);
        setPlayerCount(count);
        // 플레이어 배열 초기화 (빈 이름으로)
        const initialPlayers = Array.from({ length: count }, (_, i) => ({
            id: `P${i + 1}`,
            name: ''
        }));
        setPlayers(initialPlayers);
    };

    const handlePlayerNameChange = (index, value) => {
        const updatedPlayers = [...players];
        updatedPlayers[index].name = value;
        setPlayers(updatedPlayers);
    };

    // 플레이어 이름 자동 채우기
    const handleAutoFill = () => {
        const updatedPlayers = players.map((player, index) => ({
            ...player,
            name: player.name.trim() ? player.name : `플레이어 ${index + 1}`
        }));
        setPlayers(updatedPlayers);
    };

    // 플레이어 위치 랜덤 섞기 (Fisher-Yates 셔플)
    const handleShuffle = () => {
        // 1️⃣ 배열 복사
        const shuffledPlayers = [...players];

        // 2️⃣ 이름이 있는 선수와 빈 슬롯을 분리
        const validPlayers = shuffledPlayers.filter(p => p && p.name && p.name.trim() !== "");
        const emptyPlayers = shuffledPlayers.filter(p => !p || !p.name || p.name.trim() === "");

        // 3️⃣ 유효한 선수들만 Fisher-Yates Shuffle
        for (let i = validPlayers.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [validPlayers[i], validPlayers[j]] = [validPlayers[j], validPlayers[i]];
        }

        // 4️⃣ 빈 슬롯을 4개 단위로 묶음
        const emptyGroups = [];
        for (let i = 0; i < emptyPlayers.length; i += 4) {
            emptyGroups.push(emptyPlayers.slice(i, i + 4));
        }

        // 5️⃣ 결과 = 셔플된 선수 + 빈 그룹
        const result = [...validPlayers, ...emptyGroups.flat()];

        setPlayers(result);
    };

    // 모든 플레이어 이름 지우기
    const handleClearAll = () => {
        const clearedPlayers = players.map(player => ({
            ...player,
            name: ''
        }));
        setPlayers(clearedPlayers);
    };

    const handleGenerateTournament = () => {
        // 빈 플레이어도 허용 (bye 처리)
        const tournamentSeeds = generateTournamentSeeds(eliminationType, playerCount, players);
        setTournamentData(tournamentSeeds);
        setStep(3);

        // 더블 엘리미네이션에서만 안내 팝업 표시
        if (eliminationType === 'D') {
            // 오늘 그만보기 체크 확인
            const today = new Date().toDateString();
            const dontShowToday = sessionStorage.getItem('tournamentGuide_dontShowToday');

            if (dontShowToday !== today) {
                setShowGuideModal(true);
            }
        }

        //토너먼트 생성 로그 저장.
        ApiSave('/common/trmntlog/save', {}, {isMsgShow: false}).then(res => {});
    };

    /**********************
     * Step 3: 토너먼트 시드 생성 (tournament-structure.md 기준)
    ***********************/
    const generateTournamentSeeds = (type, count, playerList) => {
        const seeds = [];

        if (type === 'S') {
            // 싱글 엘리미네이션
            const winnerSeedCount = count - 1;

            // seedWinNo 매핑 계산 (라운드별로)
            const seedWinMapping = {};
            let remainingMatches = count / 2;
            let currentSeed = 1;

            while (remainingMatches > 0) {
                const roundStart = currentSeed;
                const roundEnd = currentSeed + remainingMatches - 1;
                const nextRoundStart = roundEnd + 1;

                for (let i = roundStart; i <= roundEnd; i++) {
                    if (remainingMatches > 1) {
                        // 두 시드가 하나의 다음 시드로 진출
                        seedWinMapping[i] = nextRoundStart + Math.floor((i - roundStart) / 2);
                    } else {
                        // 결승전
                        seedWinMapping[i] = null;
                    }
                }

                currentSeed = roundEnd + 1;
                remainingMatches = Math.floor(remainingMatches / 2);
            }

            // 시드 생성
            for (let i = 1; i <= winnerSeedCount; i++) {
                const seed = {
                    seedNo: i,
                    seedType: 'W',
                    plyrCdA: null,
                    plyrCdB: null,
                    plyrNmA: null,
                    plyrNmB: null,
                    winPlyr: null,
                    losePlyr: null,
                    winPlyrNm: null,
                    losePlyrNm: null,
                    seedWinNo: seedWinMapping[i],
                    seedLoseNo: null,
                    armType: 'R',
                    mngCd: 'TEMP',
                    mtmkgCd: `TEMP-${Date.now()}`
                };

                // 첫 라운드에 플레이어 배치
                if (i <= count / 2) {
                    const playerAIndex = (i - 1) * 2;
                    const playerBIndex = playerAIndex + 1;

                    if (playerAIndex < playerList.length && playerList[playerAIndex].name.trim()) {
                        seed.plyrCdA = playerList[playerAIndex].id;
                        seed.plyrNmA = playerList[playerAIndex].name;
                    }
                    if (playerBIndex < playerList.length && playerList[playerBIndex].name.trim()) {
                        seed.plyrCdB = playerList[playerBIndex].id;
                        seed.plyrNmB = playerList[playerBIndex].name;
                    }
                }

                seeds.push(seed);
            }
        } else if (type === 'D') {
            // 더블 엘리미네이션 - tournament-structure.md 문서 기준으로 생성
            if (count === 4) {
                // 4강 더블 엘리미네이션
                // 승자조 (Seed 1-3)
                seeds.push(
                    {
                        seedNo: 1, seedType: 'W', seedWinNo: 3, seedLoseNo: 4,
                        plyrCdA: playerList[0]?.name.trim() ? playerList[0].id : null,
                        plyrNmA: playerList[0]?.name.trim() ? playerList[0].name : null,
                        plyrCdB: playerList[1]?.name.trim() ? playerList[1].id : null,
                        plyrNmB: playerList[1]?.name.trim() ? playerList[1].name : null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    },
                    {
                        seedNo: 2, seedType: 'W', seedWinNo: 3, seedLoseNo: 4,
                        plyrCdA: playerList[2]?.name.trim() ? playerList[2].id : null,
                        plyrNmA: playerList[2]?.name.trim() ? playerList[2].name : null,
                        plyrCdB: playerList[3]?.name.trim() ? playerList[3].id : null,
                        plyrNmB: playerList[3]?.name.trim() ? playerList[3].name : null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    },
                    { seedNo: 3, seedType: 'W', seedWinNo: 6, seedLoseNo: 5, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );
                // 패자조 (Seed 4)
                seeds.push({ seedNo: 4, seedType: 'L', seedWinNo: 5, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });
                // 패자결승 (Seed 5)
                seeds.push({ seedNo: 5, seedType: 'LF', seedWinNo: 6, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });
                // 최종결승 (Seed 6)
                seeds.push({ seedNo: 6, seedType: 'GF', seedWinNo: null, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });
            } else if (count === 8) {
                // 8강 더블 엘리미네이션 (tournament-structure.md 기준)
                // 승자조 Round 1 (Seed 1-4)
                const winnerR1 = [
                    { seedNo: 1, seedWinNo: 5, seedLoseNo: 8, playerIdx: 0 },
                    { seedNo: 2, seedWinNo: 5, seedLoseNo: 8, playerIdx: 2 },
                    { seedNo: 3, seedWinNo: 6, seedLoseNo: 9, playerIdx: 4 },
                    { seedNo: 4, seedWinNo: 6, seedLoseNo: 9, playerIdx: 6 }
                ];
                winnerR1.forEach(w => {
                    seeds.push({
                        seedNo: w.seedNo, seedType: 'W', seedWinNo: w.seedWinNo, seedLoseNo: w.seedLoseNo,
                        plyrCdA: (w.playerIdx < playerList.length && playerList[w.playerIdx]?.name.trim()) ? playerList[w.playerIdx].id : null,
                        plyrNmA: (w.playerIdx < playerList.length && playerList[w.playerIdx]?.name.trim()) ? playerList[w.playerIdx].name : null,
                        plyrCdB: (w.playerIdx + 1 < playerList.length && playerList[w.playerIdx + 1]?.name.trim()) ? playerList[w.playerIdx + 1].id : null,
                        plyrNmB: (w.playerIdx + 1 < playerList.length && playerList[w.playerIdx + 1]?.name.trim()) ? playerList[w.playerIdx + 1].name : null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                });

                // 승자조 Round 2 (Seed 5-6): 준결승
                seeds.push(
                    { seedNo: 5, seedType: 'W', seedWinNo: 7, seedLoseNo: 11, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 6, seedType: 'W', seedWinNo: 7, seedLoseNo: 10, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 승자조 Round 3 (Seed 7): 결승
                seeds.push({ seedNo: 7, seedType: 'W', seedWinNo: 14, seedLoseNo: 13, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 패자조 Round 1 (Seed 8-9)
                seeds.push(
                    { seedNo: 8, seedType: 'L', seedWinNo: 10, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 9, seedType: 'L', seedWinNo: 11, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 패자조 Round 2 (Seed 10-11)
                seeds.push(
                    { seedNo: 10, seedType: 'L', seedWinNo: 12, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 11, seedType: 'L', seedWinNo: 12, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 패자조 Round 3 (Seed 12)
                seeds.push({ seedNo: 12, seedType: 'L', seedWinNo: 13, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 패자결승 (Seed 13)
                seeds.push({ seedNo: 13, seedType: 'LF', seedWinNo: 14, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 최종결승 (Seed 14)
                seeds.push({ seedNo: 14, seedType: 'GF', seedWinNo: null, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });
            } else if (count === 16) {
                // 16강 더블 엘리미네이션 (tournament-structure.md 기준)
                // 승자조 Round 1 (Seed 1-8): 16강전
                for (let i = 1; i <= 8; i++) {
                    const seedWinNo = 8 + Math.floor((i - 1) / 2) + 1; // 9-12
                    const actualSeedLoseNo = 15 + Math.floor((i - 1) / 2) + 1; // 16-19

                    const playerAIndex = (i - 1) * 2;
                    const playerBIndex = playerAIndex + 1;

                    seeds.push({
                        seedNo: i,
                        seedType: 'W',
                        seedWinNo: seedWinNo,
                        seedLoseNo: actualSeedLoseNo,
                        plyrCdA: (playerAIndex < playerList.length && playerList[playerAIndex]?.name.trim()) ? playerList[playerAIndex].id : null,
                        plyrNmA: (playerAIndex < playerList.length && playerList[playerAIndex]?.name.trim()) ? playerList[playerAIndex].name : null,
                        plyrCdB: (playerBIndex < playerList.length && playerList[playerBIndex]?.name.trim()) ? playerList[playerBIndex].id : null,
                        plyrNmB: (playerBIndex < playerList.length && playerList[playerBIndex]?.name.trim()) ? playerList[playerBIndex].name : null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                }

                // 승자조 Round 2 (Seed 9-12): 8강전
                const r2Mapping = [
                    { seedNo: 9, seedWinNo: 13, seedLoseNo: 23 },
                    { seedNo: 10, seedWinNo: 13, seedLoseNo: 22 },
                    { seedNo: 11, seedWinNo: 14, seedLoseNo: 21 },
                    { seedNo: 12, seedWinNo: 14, seedLoseNo: 20 }
                ];
                r2Mapping.forEach(m => {
                    seeds.push({
                        seedNo: m.seedNo, seedType: 'W', seedWinNo: m.seedWinNo, seedLoseNo: m.seedLoseNo,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                });

                // 승자조 Round 3 (Seed 13-14): 준결승
                seeds.push(
                    { seedNo: 13, seedType: 'W', seedWinNo: 15, seedLoseNo: 27, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 14, seedType: 'W', seedWinNo: 15, seedLoseNo: 26, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 승자조 Round 4 (Seed 15): 결승
                seeds.push({ seedNo: 15, seedType: 'W', seedWinNo: 30, seedLoseNo: 29, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 패자조 Round 1 (Seed 16-19)
                const loserR1Mapping = [
                    { seedNo: 16, seedWinNo: 20 },
                    { seedNo: 17, seedWinNo: 21 },
                    { seedNo: 18, seedWinNo: 22 },
                    { seedNo: 19, seedWinNo: 23 }
                ];
                loserR1Mapping.forEach(m => {
                    seeds.push({
                        seedNo: m.seedNo, seedType: 'L', seedWinNo: m.seedWinNo, seedLoseNo: null,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                });

                // 패자조 Round 2 (Seed 20-23)
                const loserR2Mapping = [
                    { seedNo: 20, seedWinNo: 24 },
                    { seedNo: 21, seedWinNo: 24 },
                    { seedNo: 22, seedWinNo: 25 },
                    { seedNo: 23, seedWinNo: 25 }
                ];
                loserR2Mapping.forEach(m => {
                    seeds.push({
                        seedNo: m.seedNo, seedType: 'L', seedWinNo: m.seedWinNo, seedLoseNo: null,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                });

                // 패자조 Round 3 (Seed 24-25)
                seeds.push(
                    { seedNo: 24, seedType: 'L', seedWinNo: 26, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 25, seedType: 'L', seedWinNo: 27, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 패자조 Round 4 (Seed 26-27)
                seeds.push(
                    { seedNo: 26, seedType: 'L', seedWinNo: 28, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 27, seedType: 'L', seedWinNo: 28, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 패자조 Round 5 (Seed 28)
                seeds.push({ seedNo: 28, seedType: 'L', seedWinNo: 29, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 패자결승 (Seed 29)
                seeds.push({ seedNo: 29, seedType: 'LF', seedWinNo: 30, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 최종결승 (Seed 30)
                seeds.push({ seedNo: 30, seedType: 'GF', seedWinNo: null, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });
            } else if (count === 32) {
                // 32강 더블 엘리미네이션 (tournament-structure.md 기준)
                // 총 62개 시드: 승자조 31개 + 패자조 29개 + 패자결승 1개 + 최종결승 1개

                // 승자조 Round 1 (Seed 1-16): 32강전
                for (let i = 1; i <= 16; i++) {
                    const seedWinNo = 16 + Math.floor((i - 1) / 2) + 1; // 17-24
                    const seedLoseNo = 31 + Math.floor((i - 1) / 2) + 1; // 32-39

                    const playerAIndex = (i - 1) * 2;
                    const playerBIndex = playerAIndex + 1;

                    seeds.push({
                        seedNo: i,
                        seedType: 'W',
                        seedWinNo: seedWinNo,
                        seedLoseNo: seedLoseNo,
                        plyrCdA: (playerAIndex < playerList.length && playerList[playerAIndex]?.name.trim()) ? playerList[playerAIndex].id : null,
                        plyrNmA: (playerAIndex < playerList.length && playerList[playerAIndex]?.name.trim()) ? playerList[playerAIndex].name : null,
                        plyrCdB: (playerBIndex < playerList.length && playerList[playerBIndex]?.name.trim()) ? playerList[playerBIndex].id : null,
                        plyrNmB: (playerBIndex < playerList.length && playerList[playerBIndex]?.name.trim()) ? playerList[playerBIndex].name : null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                }

                // 승자조 Round 2 (Seed 17-24): 16강전
                const r2WinnerMapping = [
                    { seedNo: 17, seedWinNo: 25, seedLoseNo: 47 },
                    { seedNo: 18, seedWinNo: 25, seedLoseNo: 46 },
                    { seedNo: 19, seedWinNo: 26, seedLoseNo: 45 },
                    { seedNo: 20, seedWinNo: 26, seedLoseNo: 44 },
                    { seedNo: 21, seedWinNo: 27, seedLoseNo: 43 },
                    { seedNo: 22, seedWinNo: 27, seedLoseNo: 42 },
                    { seedNo: 23, seedWinNo: 28, seedLoseNo: 41 },
                    { seedNo: 24, seedWinNo: 28, seedLoseNo: 40 }
                ];
                r2WinnerMapping.forEach(m => {
                    seeds.push({
                        seedNo: m.seedNo, seedType: 'W', seedWinNo: m.seedWinNo, seedLoseNo: m.seedLoseNo,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                });

                // 승자조 Round 3 (Seed 25-28): 8강전
                const r3WinnerMapping = [
                    { seedNo: 25, seedWinNo: 29, seedLoseNo: 55 },
                    { seedNo: 26, seedWinNo: 29, seedLoseNo: 54 },
                    { seedNo: 27, seedWinNo: 30, seedLoseNo: 53 },
                    { seedNo: 28, seedWinNo: 30, seedLoseNo: 52 }
                ];
                r3WinnerMapping.forEach(m => {
                    seeds.push({
                        seedNo: m.seedNo, seedType: 'W', seedWinNo: m.seedWinNo, seedLoseNo: m.seedLoseNo,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                });

                // 승자조 Round 4 (Seed 29-30): 준결승
                seeds.push(
                    { seedNo: 29, seedType: 'W', seedWinNo: 31, seedLoseNo: 59, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 30, seedType: 'W', seedWinNo: 31, seedLoseNo: 58, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 승자조 Round 5 (Seed 31): 결승
                seeds.push({ seedNo: 31, seedType: 'W', seedWinNo: 62, seedLoseNo: 61, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 패자조 Round 1 (Seed 32-39): 승자조 R1 패자끼리
                for (let i = 32; i <= 39; i++) {
                    const seedWinNo = 40 + (i - 32); // 40-47
                    seeds.push({
                        seedNo: i, seedType: 'L', seedWinNo: seedWinNo, seedLoseNo: null,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                }

                // 패자조 Round 2 (Seed 40-47): 패자조 R1 승자 vs 승자조 R2 패자
                for (let i = 40; i <= 47; i++) {
                    const seedWinNo = 48 + Math.floor((i - 40) / 2); // 48-51
                    seeds.push({
                        seedNo: i, seedType: 'L', seedWinNo: seedWinNo, seedLoseNo: null,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                }

                // 패자조 Round 3 (Seed 48-51): 패자조 R2 승자끼리
                for (let i = 48; i <= 51; i++) {
                    const seedWinNo = 52 + (i - 48); // 52-55
                    seeds.push({
                        seedNo: i, seedType: 'L', seedWinNo: seedWinNo, seedLoseNo: null,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                }

                // 패자조 Round 4 (Seed 52-55): 패자조 R3 승자 vs 승자조 R3 패자
                const r4LoserMapping = [
                    { seedNo: 52, seedWinNo: 56 },
                    { seedNo: 53, seedWinNo: 56 },
                    { seedNo: 54, seedWinNo: 57 },
                    { seedNo: 55, seedWinNo: 57 }
                ];
                r4LoserMapping.forEach(m => {
                    seeds.push({
                        seedNo: m.seedNo, seedType: 'L', seedWinNo: m.seedWinNo, seedLoseNo: null,
                        plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null,
                        winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null,
                        armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}`
                    });
                });

                // 패자조 Round 5 (Seed 56-57): 패자조 R4 승자끼리
                seeds.push(
                    { seedNo: 56, seedType: 'L', seedWinNo: 58, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 57, seedType: 'L', seedWinNo: 59, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 패자조 Round 6 (Seed 58-59): 패자조 R5 승자 vs 승자조 R4 패자
                seeds.push(
                    { seedNo: 58, seedType: 'L', seedWinNo: 60, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` },
                    { seedNo: 59, seedType: 'L', seedWinNo: 60, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` }
                );

                // 패자조 Round 7 (Seed 60): 패자조 R6 승자끼리
                seeds.push({ seedNo: 60, seedType: 'L', seedWinNo: 61, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 패자결승 (Seed 61)
                seeds.push({ seedNo: 61, seedType: 'LF', seedWinNo: 62, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });

                // 최종결승 (Seed 62)
                seeds.push({ seedNo: 62, seedType: 'GF', seedWinNo: null, seedLoseNo: null, plyrCdA: null, plyrNmA: null, plyrCdB: null, plyrNmB: null, winPlyr: null, losePlyr: null, winPlyrNm: null, losePlyrNm: null, armType: 'R', mngCd: 'TEMP', mtmkgCd: `TEMP-${Date.now()}` });
            }
        }

        return seeds;
    };

    /**********************
     * 토너먼트 렌더링 함수들 (comp-form-trmntBracket.js 참고)
    ***********************/
    // SEED_TYPE별로 데이터 분류
    const groupBySeedType = () => {
        const grouped = {
            winner: [],
            loser: [],
            loserFinal: [],
            grandFinal: []
        };

        tournamentData.forEach(seed => {
            if (seed.seedType === 'W') {
                grouped.winner.push(seed);
            } else if (seed.seedType === 'L') {
                grouped.loser.push(seed);
            } else if (seed.seedType === 'LF') {
                grouped.loserFinal.push(seed);
            } else if (seed.seedType === 'GF') {
                grouped.grandFinal.push(seed);
            }
        });

        return grouped;
    };

    // 승자조 라운드별 그룹화
    const groupWinnerByRound = (matches) => {
        if (!matches || matches.length === 0) return [];

        const sortedMatches = [...matches].sort((a, b) => a.seedNo - b.seedNo);
        const rounds = [];
        let currentRoundSize = Math.ceil(sortedMatches.length / 2);
        let matchIndex = 0;

        while (matchIndex < sortedMatches.length) {
            const round = [];
            const takeCount = Math.min(currentRoundSize, sortedMatches.length - matchIndex);

            for (let i = 0; i < takeCount; i++) {
                round.push(sortedMatches[matchIndex++]);
            }

            rounds.push(round);
            currentRoundSize = Math.ceil(currentRoundSize / 2);
        }

        return rounds;
    };

    // 패자조 라운드별 그룹화
    const groupLoserByRound = (matches) => {
        if (!matches || matches.length === 0) return [];

        const sortedMatches = [...matches].sort((a, b) => a.seedNo - b.seedNo);
        const firstLoserSeedNo = sortedMatches[0].seedNo;

        const loserRoundRanges = {
            4: [
                { start: 4, end: 4, round: 1 }
            ],
            8: [
                { start: 8, end: 9, round: 1 },
                { start: 10, end: 11, round: 2 },
                { start: 12, end: 12, round: 3 }
            ],
            16: [
                { start: 16, end: 19, round: 1 },
                { start: 20, end: 23, round: 2 },
                { start: 24, end: 25, round: 3 },
                { start: 26, end: 27, round: 4 },
                { start: 28, end: 28, round: 5 }
            ],
            32: [
                { start: 32, end: 39, round: 1 },
                { start: 40, end: 47, round: 2 },
                { start: 48, end: 51, round: 3 },
                { start: 52, end: 55, round: 4 },
                { start: 56, end: 57, round: 5 },
                { start: 58, end: 59, round: 6 },
                { start: 60, end: 60, round: 7 }
            ]
        };

        let tournamentSize = 4;
        if (firstLoserSeedNo >= 32) {
            tournamentSize = 32;
        } else if (firstLoserSeedNo >= 16) {
            tournamentSize = 16;
        } else if (firstLoserSeedNo >= 8) {
            tournamentSize = 8;
        }

        const roundRanges = loserRoundRanges[tournamentSize];

        const loserWithRound = sortedMatches.map(loserMatch => {
            let assignedRound = 0;
            for (const range of roundRanges) {
                if (loserMatch.seedNo >= range.start && loserMatch.seedNo <= range.end) {
                    assignedRound = range.round;
                    break;
                }
            }

            return {
                ...loserMatch,
                sourceRound: assignedRound
            };
        });

        const roundMap = new Map();
        loserWithRound.forEach(match => {
            if (!roundMap.has(match.sourceRound)) {
                roundMap.set(match.sourceRound, []);
            }
            roundMap.get(match.sourceRound).push(match);
        });

        const rounds = Array.from(roundMap.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([_, matchesInRound]) => matchesInRound.sort((a, b) => a.seedNo - b.seedNo));

        return rounds;
    };

    /**
     * 시드의 라운드 번호 계산 함수
     * @param {Object} seed - 시드 객체
     * @param {Array} allSeeds - 전체 시드 데이터
     * @returns {number} 라운드 번호
     */
    const calculateRound = (seed, allSeeds) => {
        if (seed.seedType === 'W') {
            // 승자조: seedNo 기반으로 라운드 계산
            const winnerSeeds = allSeeds.filter(s => s.seedType === 'W').sort((a, b) => a.seedNo - b.seedNo);
            const totalMatches = winnerSeeds.length;

            // 전체 토너먼트 크기 추정 (4, 8, 16, 32)
            let tournamentSize = 4;
            if (totalMatches >= 31) tournamentSize = 32;
            else if (totalMatches >= 15) tournamentSize = 16;
            else if (totalMatches >= 7) tournamentSize = 8;

            // 각 라운드의 시작 seedNo 계산
            const roundStarts = [];
            let start = 1;
            let size = tournamentSize / 2;
            while (size >= 1) {
                roundStarts.push(start);
                start += size;
                size = size / 2;
            }

            // seedNo가 어느 라운드에 속하는지 찾기
            for (let i = roundStarts.length - 1; i >= 0; i--) {
                if (seed.seedNo >= roundStarts[i]) {
                    return i + 1;
                }
            }
            return 1;
        } else if (seed.seedType === 'L') {
            // 패자조: seedNo 범위 기반으로 라운드 계산
            const loserSeeds = allSeeds.filter(s => s.seedType === 'L').sort((a, b) => a.seedNo - b.seedNo);
            if (loserSeeds.length === 0) return 0;

            const firstLoserSeedNo = loserSeeds[0].seedNo;

            const loserRoundRanges = {
                4: [
                    { start: 4, end: 4, round: 1 }
                ],
                8: [
                    { start: 8, end: 9, round: 1 },
                    { start: 10, end: 11, round: 2 },
                    { start: 12, end: 12, round: 3 }
                ],
                16: [
                    { start: 16, end: 19, round: 1 },
                    { start: 20, end: 23, round: 2 },
                    { start: 24, end: 25, round: 3 },
                    { start: 26, end: 27, round: 4 },
                    { start: 28, end: 28, round: 5 }
                ],
                32: [
                    { start: 32, end: 39, round: 1 },
                    { start: 40, end: 47, round: 2 },
                    { start: 48, end: 51, round: 3 },
                    { start: 52, end: 55, round: 4 },
                    { start: 56, end: 57, round: 5 },
                    { start: 58, end: 59, round: 6 },
                    { start: 60, end: 60, round: 7 }
                ]
            };

            let tournamentSize = 4;
            if (firstLoserSeedNo >= 32) tournamentSize = 32;
            else if (firstLoserSeedNo >= 16) tournamentSize = 16;
            else if (firstLoserSeedNo >= 8) tournamentSize = 8;

            const roundRanges = loserRoundRanges[tournamentSize];
            for (const range of roundRanges) {
                if (seed.seedNo >= range.start && seed.seedNo <= range.end) {
                    return range.round;
                }
            }
        }
        return 0;
    };

    /**
     * 승자 이동 위치 계산 함수
     * @param {Object} currentSeed - 현재 시드
     * @param {Array} allSeeds - 전체 시드 데이터
     * @returns {string} 'A' 또는 'B'
     */
    const calculateWinnerPosition = (currentSeed, allSeeds) => {
        // 패자결승(LF)의 승자는 최종결승(GF)의 B로 이동
        if (currentSeed.seedType === 'LF') {
            return 'B';
        }

        // 패자조인 경우
        if (currentSeed.seedType === 'L') {
            const currentRound = calculateRound(currentSeed, allSeeds);

            // 현재 라운드의 시드 개수
            const currentRoundSeeds = allSeeds.filter(s => {
                if (s.seedType !== 'L') return false;
                const round = calculateRound(s, allSeeds);
                return round === currentRound;
            }).length;

            // 다음 라운드의 시드 개수 (seedWinNo가 속한 라운드)
            const nextSeed = allSeeds.find(s => s.seedNo === currentSeed.seedWinNo);
            if (nextSeed) {
                const nextRound = calculateRound(nextSeed, allSeeds);
                const nextRoundSeeds = allSeeds.filter(s => {
                    if (s.seedType !== nextSeed.seedType) return false;
                    const round = calculateRound(s, allSeeds);
                    return round === nextRound;
                }).length;

                // 다음 라운드 시드 개수가 현재와 동일하면 A로 이동
                if (nextRoundSeeds === currentRoundSeeds) {
                    return 'A';
                }

                // 다음 라운드 시드 개수가 현재보다 작으면 계산 로직 적용
                if (nextRoundSeeds < currentRoundSeeds) {
                    return currentSeed.seedNo % 2 === 1 ? 'A' : 'B';
                }
            }
        }

        // 승자조 또는 기본 로직
        return currentSeed.seedNo % 2 === 1 ? 'A' : 'B';
    };

    /**
     * 패자 이동 위치 계산 함수
     * @param {Object} currentSeed - 현재 시드
     * @param {Array} allSeeds - 전체 시드 데이터
     * @returns {string} 'A' 또는 'B'
     */
    const calculateLoserPosition = (currentSeed, allSeeds) => {
        const currentRound = calculateRound(currentSeed, allSeeds);

        // 승자조 1라운드가 아닌 경우 B로 이동
        if (currentSeed.seedType === 'W' && currentRound > 1) {
            return 'B';
        }

        // 승자조 1라운드이거나 기본 로직
        return currentSeed.seedNo % 2 === 1 ? 'A' : 'B';
    };

    // 선수 클릭 시 승자 선택
    const handlePlayerClick = (seed, playerType) => {
        //Home에서 결과보는경우 승자 선택 기능 사용하지 않음.
        if (isFromHome) { return; }

        const winnerId = playerType === 'A' ? seed.plyrCdA : seed.plyrCdB;
        const winnerNm = playerType === 'A' ? seed.plyrNmA : seed.plyrNmB;
        const loserId = playerType === 'A' ? seed.plyrCdB : seed.plyrCdA;
        const loserNm = playerType === 'A' ? seed.plyrNmB : seed.plyrNmA;

        if (!winnerId || winnerId === 'null') return;
        if (seed.winPlyr === winnerId) return;

        // 현재 시드 업데이트
        const updatedData = tournamentData.map(s => {
            if (s.seedNo === seed.seedNo && s.seedType === seed.seedType) {
                return {
                    ...s,
                    winPlyr: winnerId,
                    winPlyrNm: winnerNm,
                    losePlyr: loserId,
                    losePlyrNm: loserNm
                };
            }
            return s;
        });

        // 승자를 다음 라운드로 진출
        if (seed.seedWinNo) {
            const nextSeed = updatedData.find(s => s.seedNo === seed.seedWinNo);

            if (nextSeed) {
                const position = calculateWinnerPosition(seed, updatedData);

                if (position === 'A') {
                    nextSeed.plyrCdA = winnerId;
                    nextSeed.plyrNmA = winnerNm;
                } else {
                    nextSeed.plyrCdB = winnerId;
                    nextSeed.plyrNmB = winnerNm;
                }
            }
        }

        // 더블 엘리미네이션: 패자를 패자조로 진출
        if (eliminationType === 'D' && seed.seedType === 'W' && seed.seedLoseNo && loserId && loserId !== 'null') {
            const loserNextSeed = updatedData.find(s => s.seedNo === seed.seedLoseNo);

            if (loserNextSeed) {
                const position = calculateLoserPosition(seed, updatedData);

                if (position === 'A') {
                    loserNextSeed.plyrCdA = loserId;
                    loserNextSeed.plyrNmA = loserNm;
                } else {
                    loserNextSeed.plyrCdB = loserId;
                    loserNextSeed.plyrNmB = loserNm;
                }
            }
        }

        setTournamentData(updatedData);
    };

    // 매치 렌더링
    const renderMatch = (seed, roundIndex) => {
        const isPlayerAWinner = seed.winPlyr === seed.plyrCdA;
        const isPlayerBWinner = seed.winPlyr === seed.plyrCdB;
        const hasWinner = seed.winPlyr !== null && seed.winPlyr !== undefined;
        const canSelect = seed.plyrCdA || seed.plyrCdB;
        const isClickable = canSelect;

        return (
            <div
                key={`${seed.seedType}-${seed.seedNo}`}
                className={`bracket-match ${hasWinner ? 'completed' : ''} ${isClickable ? 'selectable' : ''}`}
                data-seed={seed.seedNo}
                data-round={roundIndex}
            >
                <div className="match-number" style={{color: theme === 'dark' ? '#a0a0a0' : '#6c757d'}}>
                    <span style={{ fontSize: '15px' }}>#{seed.seedNo}</span>
                    {!seed.plyrCdA && !seed.plyrCdB && (
                        <div className="match-waiting">
                            <small style={{ fontSize: '9px' }}>대기 중</small>
                        </div>
                    )}
                </div>

                <div className="match-players-container">
                    <div
                        className={`match-player ${isPlayerAWinner ? 'winner' : ''} ${!seed.plyrCdA || seed.plyrCdA === 'null' ? 'bye' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && handlePlayerClick(seed, 'A')}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                        <span className="player-name">
                            {seed.plyrNmA || 'BYE'}
                        </span>
                        {isPlayerAWinner && <span className="winner-icon">👑</span>}
                    </div>

                    <div
                        className={`match-player ${isPlayerBWinner ? 'winner' : ''} ${!seed.plyrCdB || seed.plyrCdB === 'null' ? 'bye' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && handlePlayerClick(seed, 'B')}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                        <span className="player-name">
                            {seed.plyrNmB || 'BYE'}
                        </span>
                        {isPlayerBWinner && <span className="winner-icon">👑</span>}
                    </div>
                </div>
            </div>
        );
    };

    // 싱글 엘리미네이션 렌더링
    const renderSingleElimination = () => {
        const grouped = groupBySeedType();
        const winnerRounds = groupWinnerByRound(grouped.winner);

        return (
            <div className={`tournament-bracket-wrapper ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}>
                <div className="section-header bg-primary text-white p-2 rounded">
                    <h5 className="mb-0">🏆 싱글 엘리미네이션</h5>
                </div>

                <div className="bracket-rounds-container">
                    {winnerRounds.map((round, roundIndex) => (
                        <div key={`round-${roundIndex}`} className="bracket-round">
                            <div className="round-title">
                                <span className="badge bg-primary">
                                    {roundIndex === winnerRounds.length - 1 ? '결승' : `Round ${roundIndex + 1}`}
                                </span>
                            </div>
                            <div className="round-matches">
                                {round.map(seed => renderMatch(seed, roundIndex))}
                            </div>
                            {roundIndex < winnerRounds.length - 1 && (
                                <div className="round-connector">
                                    <svg className="connector-lines" width="100%" height="100%">
                                        {round.map((seed, idx) => {
                                            if (idx % 2 === 0 && idx + 1 < round.length) {
                                                return (
                                                    <g key={`connector-${idx}`}>
                                                        <line x1="0" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                              x2="50%" y2={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                              stroke="#0d6efd" strokeWidth="2"/>
                                                        <line x1="0" y1={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                              x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                              stroke="#0d6efd" strokeWidth="2"/>
                                                        <line x1="50%" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                              x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                              stroke="#0d6efd" strokeWidth="2"/>
                                                        <line x1="50%"
                                                              y1={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                              x2="100%"
                                                              y2={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                              stroke="#0d6efd" strokeWidth="2"/>
                                                    </g>
                                                );
                                            }
                                            return null;
                                        })}
                                    </svg>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // 더블 엘리미네이션 렌더링
    const renderDoubleElimination = () => {
        const grouped = groupBySeedType();
        const winnerRounds = groupWinnerByRound(grouped.winner);
        const loserRounds = groupLoserByRound(grouped.loser);
        const loserFinals = grouped.loserFinal;
        const grandFinals = grouped.grandFinal;

        return (
            <div className={`tournament-bracket-wrapper double-elimination ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}>
                {/* 승자조 */}
                {/* Home에서 온 경우 대회 정보 헤더 */}
                {isFromHome && competitionInfo && (<>
                    <div className="bracket-section d-flex gap-1 flex-wrap mb-2 pb-0">
                        {competitionInfo.dvsnNm && (
                            <span className="badge bg-info" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                                <i className="bi bi-tag-fill me-1"></i>
                                {competitionInfo.dvsnNm}
                            </span>
                        )}
                        {competitionInfo.wetclsNm && (
                            <span className="badge bg-secondary" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                                <i className="bi bi-speedometer2 me-1"></i>
                                {competitionInfo.wetclsNm}
                            </span>
                        )}
                        <span className="badge bg-primary" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                            <i className="bi bi-hand-index-thumb me-1"></i>
                            {getArmTypeNm(competitionInfo.armType)}
                        </span>
                        <span className={`badge ${competitionInfo.mtmkgStat === 'E' ? 'bg-secondary' : competitionInfo.mtmkgStat === 'C' || competitionInfo.mtmkgStat === 'R' ? 'bg-warning text-dark' : 'bg-success'}`} style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                            {getStatusNm(competitionInfo.mtmkgStat)}
                        </span>
                    </div>
                </>)}
                <div className="bracket-section mb-4">
                    <div className="section-header bg-success text-white p-2 rounded">
                        <h5 className="mb-0">🏆 승자조 (Winner Bracket)</h5>
                    </div>
                    <div className="bracket-rounds-container mt-2">
                        {winnerRounds.map((round, roundIndex) => {
                            let roundName = '';
                            const totalRounds = winnerRounds.length;

                            if (roundIndex === totalRounds - 1) {
                                roundName = 'W-결승';
                            } else if (roundIndex === totalRounds - 2) {
                                roundName = 'W-준결승';
                            } else if (roundIndex === totalRounds - 3) {
                                roundName = 'W-8강';
                            } else if (roundIndex === totalRounds - 4) {
                                roundName = 'W-16강';
                            } else if (roundIndex === totalRounds - 5) {
                                roundName = 'W-32강';
                            } else {
                                roundName = `W-R${roundIndex + 1}`;
                            }

                            return (
                            <div key={`winner-round-${roundIndex}`} className="bracket-round">
                                <div className="round-title">
                                    <span className="badge bg-success">{roundName}</span>
                                </div>
                                <div className="round-matches">
                                    {round.map(seed => renderMatch(seed, roundIndex))}
                                </div>
                                {roundIndex < winnerRounds.length - 1 && (
                                    <div className="round-connector">
                                        <svg className="connector-lines" width="100%" height="100%">
                                            {round.map((seed, idx) => {
                                                if (idx % 2 === 0 && idx + 1 < round.length) {
                                                    return (
                                                        <g key={`w-connector-${idx}`}>
                                                            <line x1="0" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="0" y1={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="50%" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="50%"
                                                                  y1={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                                  x2="100%"
                                                                  y2={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                        </g>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </svg>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </div>

                {/* 패자조 */}
                {loserRounds.length > 0 && (
                    <div className="bracket-section mb-4">
                        <div className="section-header bg-warning text-white p-2 rounded">
                            <h5 className="mb-0">⚔️ 패자조 (Loser Bracket)</h5>
                        </div>
                        <div className="bracket-rounds-container mt-2">
                            {loserRounds.map((round, roundIndex) => (
                                <div key={`loser-round-${roundIndex}`} className="bracket-round">
                                    <div className="round-title">
                                        <span className="badge bg-warning text-dark">L-R{roundIndex + 1}</span>
                                    </div>
                                    <div className="round-matches">
                                        {round.map(seed => renderMatch(seed, roundIndex))}
                                    </div>
                                    {roundIndex < loserRounds.length - 1 && (
                                        <div className="round-connector">
                                            <svg className="connector-lines" width="100%" height="100%">
                                                {round.map((currentSeed, currentIdx) => {
                                                    if (!currentSeed.seedWinNo) return null;

                                                    let targetRoundIndex = -1;
                                                    let targetMatchIndex = -1;

                                                    for (let r = roundIndex + 1; r < loserRounds.length; r++) {
                                                        const idx = loserRounds[r].findIndex(s => s.seedNo === currentSeed.seedWinNo);
                                                        if (idx >= 0) {
                                                            targetRoundIndex = r;
                                                            targetMatchIndex = idx;
                                                            break;
                                                        }
                                                    }

                                                    if (targetRoundIndex === roundIndex + 1 && targetMatchIndex >= 0) {
                                                        const nextRound = loserRounds[targetRoundIndex];
                                                        const currentY = (currentIdx / round.length) * 100 + (50 / round.length);
                                                        const nextY = (targetMatchIndex / nextRound.length) * 100 + (50 / nextRound.length);

                                                        return (
                                                            <g key={`l-connector-${currentIdx}`}>
                                                                <line x1="0" y1={`${currentY}%`} x2="50%" y2={`${currentY}%`}
                                                                      stroke="#ffc107" strokeWidth="2"/>
                                                                <line x1="50%" y1={`${currentY}%`} x2="50%" y2={`${nextY}%`}
                                                                      stroke="#ffc107" strokeWidth="2"/>
                                                                <line x1="50%" y1={`${nextY}%`} x2="100%" y2={`${nextY}%`}
                                                                      stroke="#ffc107" strokeWidth="2"/>
                                                            </g>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 패자결승 */}
                {loserFinals.length > 0 && (
                    <div className="bracket-section mb-4">
                        <div className="section-header bg-info text-white p-2 rounded">
                            <h5 className="mb-0">🥈 패자결승 (Loser Final)</h5>
                        </div>
                        <div className="finals-container mt-2">
                            <div className="finals-matches">
                                {loserFinals.map((seed, idx) => renderMatch(seed, idx))}
                            </div>
                        </div>
                    </div>
                )}

                {/* 최종결승 */}
                {grandFinals.length > 0 && (
                    <div className="bracket-section">
                        <div className="section-header bg-danger text-white p-2 rounded">
                            <h5 className="mb-0">🏅 최종결승 (Grand Final)</h5>
                        </div>
                        <div className="finals-container mt-2">
                            <div className="finals-matches">
                                {grandFinals.map((seed, idx) => renderMatch(seed, idx))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    /**********************
     * 최종 순위 계산
    ***********************/
    const calculateRankings = () => {
        const rankings = {
            first: null,
            second: null,
            third: [],
            fourth: [],
            fifth: [],
            sixth: []
        };

        if (eliminationType === 'D') {
            // 더블 엘리미네이션
            const grandFinal = tournamentData.find(seed => seed.seedType === 'GF');
            const loserFinal = tournamentData.find(seed => seed.seedType === 'LF');

            // 1등: 최종결승 승자
            if (grandFinal && grandFinal.winPlyr) {
                rankings.first = {
                    id: grandFinal.winPlyr,
                    name: grandFinal.winPlyrNm
                };
                // 2등: 최종결승 패자
                if (grandFinal.losePlyr) {
                    rankings.second = {
                        id: grandFinal.losePlyr,
                        name: grandFinal.losePlyrNm
                    };
                }
            }

            // 3등: 패자결승 패자
            if (loserFinal && loserFinal.losePlyr) {
                rankings.third.push({
                    id: loserFinal.losePlyr,
                    name: loserFinal.losePlyrNm
                });
            }

            // 4-6위: 패자조를 라운드별로 그룹핑하여 순위 결정
            const grouped = groupBySeedType();
            const loserRounds = groupLoserByRound(grouped.loser);

            // 라운드를 역순으로 순회 (가장 높은 라운드부터)
            const rankKeys = ['fourth', 'fifth', 'sixth'];
            let rankIndex = 0;

            for (let i = loserRounds.length - 1; i >= 0 && rankIndex < rankKeys.length; i--) {
                const roundMatches = loserRounds[i];

                roundMatches.forEach(seed => {
                    if (seed.losePlyr && seed.losePlyr !== 'null') {
                        const player = {
                            id: seed.losePlyr,
                            name: seed.losePlyrNm
                        };

                        // 이미 상위 순위에 포함된 선수는 제외
                        const isAlreadyRanked =
                            (rankings.first && rankings.first.id === player.id) ||
                            (rankings.second && rankings.second.id === player.id) ||
                            rankings.third.some(p => p.id === player.id);

                        if (!isAlreadyRanked && rankIndex < rankKeys.length) {
                            rankings[rankKeys[rankIndex]].push(player);
                        }
                    }
                });

                // 이번 라운드에서 패자가 있었다면 다음 순위로
                if (rankings[rankKeys[rankIndex]].length > 0) {
                    rankIndex++;
                }
            }
        } else if (eliminationType === 'S') {
            // 싱글 엘리미네이션
            const finalMatch = tournamentData.find(seed => seed.seedType === 'W' && seed.seedWinNo === null);

            if (finalMatch && finalMatch.winPlyr) {
                rankings.first = {
                    id: finalMatch.winPlyr,
                    name: finalMatch.winPlyrNm
                };
                if (finalMatch.losePlyr) {
                    rankings.second = {
                        id: finalMatch.losePlyr,
                        name: finalMatch.losePlyrNm
                    };
                }
            }

            // 준결승 패자들을 공동 3위로
            const grouped = groupBySeedType();
            const winnerRounds = groupWinnerByRound(grouped.winner);
            if (winnerRounds.length >= 2) {
                const semiFinals = winnerRounds[winnerRounds.length - 2];
                semiFinals.forEach(seed => {
                    if (seed.losePlyr && seed.losePlyr !== finalMatch?.losePlyr) {
                        rankings.third.push({
                            id: seed.losePlyr,
                            name: seed.losePlyrNm
                        });
                    }
                });
            }
        }

        return rankings;
    };

    // 순위 렌더링
    const renderRankings = () => {
        const rankings = calculateRankings();
        const hasAnyRanking = rankings.first || rankings.second || rankings.third.length > 0;

        return (
            <div className="card shadow-sm sticky-top mb-4" style={{
                top: '20px',
                backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                border: theme === 'dark' ? '1px solid rgba(255, 215, 0, 0.2)' : '1px solid rgba(0, 0, 0, 0.125)',
                transition: 'all 0.3s ease'
            }}>
                <div className="card-header d-flex align-items-center"
                    style={{
                        background: theme === 'dark'
                            ? 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        padding: '0.75rem',
                        transition: 'all 0.3s ease'
                    }}
                    onClick={() => setIsRankingExpanded(!isRankingExpanded)}
                >
                    <button
                        className="btn btn-sm p-0 me-2"
                        style={{
                            color: '#ffffff',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '1.3rem',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsRankingExpanded(!isRankingExpanded);
                        }}
                    >
                        ☰
                    </button>
                    {isRankingExpanded ? (
                        <h5 className="mb-0 flex-grow-1 text-center" style={{ color: '#ffffff', fontWeight: 'bold' }}>🏆 최종 순위</h5>
                    ) : (
                        <div style={{ fontSize: '1.5rem', display: 'flex', alignItems: 'center' }}>🏆</div>
                    )}
                </div>
                {isRankingExpanded && (
                    <div className="card-body p-3" style={{ paddingBottom: '1.5rem' }}>
                    {/* 1등 */}
                    {rankings.first && (
                        <div className="ranking-item mb-3 p-3 rounded" style={{ background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)', border: '2px solid #ffc107' }}>
                            <div className="d-flex align-items-center">
                                <div className="ranking-medal me-3" style={{ fontSize: '3rem' }}>🥇</div>
                                <div className="flex-grow-1">
                                    <div className="ranking-position fw-bold" style={{ fontSize: '0.9rem', color: '#856404' }}>1등</div>
                                    <div className="ranking-name fw-bold" style={{ fontSize: '1.3rem', color: '#212529' }}>{rankings.first.name}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 2등 */}
                    {rankings.second && (
                        <div className="ranking-item mb-3 p-3 rounded" style={{ background: 'linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)', border: '2px solid #a8a8a8' }}>
                            <div className="d-flex align-items-center">
                                <div className="ranking-medal me-3" style={{ fontSize: '2.5rem' }}>🥈</div>
                                <div className="flex-grow-1">
                                    <div className="ranking-position fw-bold" style={{ fontSize: '0.85rem', color: '#6c757d' }}>2등</div>
                                    <div className="ranking-name fw-bold" style={{ fontSize: '1.2rem', color: '#212529' }}>{rankings.second.name}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 3등 */}
                    {rankings.third.length > 0 && (
                        <div className="ranking-item mb-2 p-3 rounded" style={{ background: 'linear-gradient(135deg, #cd7f32 0%, #e8b887 100%)', border: '2px solid #b8732a' }}>
                            <div className="d-flex align-items-center">
                                <div className="ranking-medal me-3" style={{ fontSize: '2rem' }}>🥉</div>
                                <div className="flex-grow-1">
                                    <div className="ranking-position fw-bold" style={{ fontSize: '0.85rem', color: '#7a4f1e' }}>3등</div>
                                    {rankings.third.map((player, idx) => (
                                        <div key={idx} className="ranking-name fw-bold" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                            {player.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 4등 (더블 엘리미네이션만) */}
                    {eliminationType === 'D' && rankings.fourth && rankings.fourth.length > 0 && (
                        <div className="ranking-item mb-2 p-2 rounded" style={{ background: '#f0f0f0', border: '1px solid #d0d0d0' }}>
                            <div className="d-flex align-items-center">
                                <div className="ranking-medal me-2" style={{ fontSize: '1.3rem' }}>4️⃣</div>
                                <div className="flex-grow-1">
                                    <div className="ranking-position fw-bold" style={{ fontSize: '0.75rem', color: '#6c757d' }}>4등</div>
                                    {rankings.fourth.map((player, idx) => (
                                        <div key={idx} className="ranking-name" style={{ fontSize: '0.95rem', color: '#212529' }}>
                                            {player.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 5등 (더블 엘리미네이션만) */}
                    {eliminationType === 'D' && rankings.fifth && rankings.fifth.length > 0 && (
                        <div className="ranking-item mb-2 p-2 rounded" style={{ background: '#f5f5f5', border: '1px solid #d5d5d5' }}>
                            <div className="d-flex align-items-center">
                                <div className="ranking-medal me-2" style={{ fontSize: '1.3rem' }}>5️⃣</div>
                                <div className="flex-grow-1">
                                    <div className="ranking-position fw-bold" style={{ fontSize: '0.75rem', color: '#6c757d' }}>5등</div>
                                    {rankings.fifth.map((player, idx) => (
                                        <div key={idx} className="ranking-name" style={{ fontSize: '0.95rem', color: '#212529' }}>
                                            {player.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 6등 (더블 엘리미네이션만) */}
                    {eliminationType === 'D' && rankings.sixth && rankings.sixth.length > 0 && (
                        <div className="ranking-item mb-2 p-2 rounded" style={{ background: '#fafafa', border: '1px solid #dadada' }}>
                            <div className="d-flex align-items-center">
                                <div className="ranking-medal me-2" style={{ fontSize: '1.3rem' }}>6️⃣</div>
                                <div className="flex-grow-1">
                                    <div className="ranking-position fw-bold" style={{ fontSize: '0.75rem', color: '#6c757d' }}>6등</div>
                                    {rankings.sixth.map((player, idx) => (
                                        <div key={idx} className="ranking-name" style={{ fontSize: '0.95rem', color: '#212529' }}>
                                            {player.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {!hasAnyRanking && (
                        <div className="text-center py-4" style={{color: theme === 'dark' ? '#a0a0a0' : '#6c757d'}}>
                            <i className="bi bi-trophy" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                            <p className="mt-2 mb-0" style={{ fontSize: '0.9rem' }}>토너먼트를 완료하면<br/>순위가 표시됩니다</p>
                        </div>
                    )}
                    </div>
                )}
            </div>
        );
    };

    // 초기화
    const handleReset = () => {
        if (window.confirm('토너먼트를 초기화하시겠습니까?')) {
            setStep(1);
            setEliminationType('');
            setPlayerCount(0);
            setPlayers([]);
            setTournamentData([]);
            setIsRankingVisible(true);
            setIsRankingExpanded(true);
            // SessionStorage도 삭제
            sessionStorage.removeItem(STORAGE_KEY);
        }
    };

    //토너먼트 상세 Bracket 정보 조회. (home에서 상세 대진 클릭하여 이동한경우 사용)
    const getTournamentData = (mngCd, mtmkgCd, armType) => {
        if (!mngCd || !mtmkgCd || !armType) return;

        ApiSearch('/ve/competition/trmntBracket/search/list', {
            mngCd: mngCd,
            mtmkgCd: mtmkgCd,
            armType: armType
        }).then(res => {
            if (res && res.success) {
                const trmntData = res.data.dataList || [];
                const compInfo = trmntData[0] || {};

                // 대회 정보 저장
                setCompetitionInfo(compInfo);

                // 토너먼트 데이터 저장
                setTournamentData(trmntData);

                setEliminationType(compInfo.mtmkgTycd); //S:싱글, D:더블

                // 플레이어 수 계산 (첫 라운드 플레이어 수)
                const firstRoundSeeds = trmntData.filter(seed =>
                    seed.seedType === 'W' && (seed.plyrCdA || seed.plyrNmA || seed.plyrCdB || seed.plyrNmB)
                );
                const playerNames = new Set();
                firstRoundSeeds.forEach(seed => {
                    if (seed.plyrNmA) playerNames.add(seed.plyrNmA);
                    if (seed.plyrNmB) playerNames.add(seed.plyrNmB);
                });
                setPlayerCount(playerNames.size);

                // 3단계로 이동
                setStep(3);
            }
        });
    };

    // Home에서 온 경우 자동으로 토너먼트 데이터 로드
    useEffect(() => {
        if (isFromHome) {
            getTournamentData(mngCdFromUrl, mtmkgCdFromUrl, armTypeFromUrl);
        }
    }, [mngCdFromUrl, mtmkgCdFromUrl, armTypeFromUrl, isFromHome]);

    // 상태코드를 한글로 변환
    const getStatusNm = (statusCd) => {
        if (statusCd === 'C' || statusCd === 'R') {
            return '대기중';
        } else if (statusCd === 'E') {
            return '종료';
        } else {
            return '진행중';
        }
    };

    // 팔 타입을 한글로 변환
    const getArmTypeNm = (armType) => {
        if (armType === 'L') {
            return '왼팔';
        } else if (armType === 'R') {
            return '오른팔';
        }
        return armType;
    };

    // Home으로 돌아가기
    const handleGoHome = () => {
        // sessionStorage 초기화
        sessionStorage.removeItem(STORAGE_KEY);
        navigate('/');
    };

    return (
        <div className={theme === 'dark' ? 'competition-page-bg-dark' : 'competition-page-bg'}>
            <div className="container-fluid" style={{
                paddingTop: '1.5rem',
                paddingBottom: '6rem',
                color: theme === 'dark' ? '#e0e0e0' : 'inherit'
            }}>
                {/* Home에서 온 경우 대회 정보 헤더 */}
                {isFromHome && competitionInfo && (
                    <div className="mt-2 mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <h3 className="mb-0" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                                    {competitionInfo.mtmkgNm || '-'}
                                </h3>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <span className="badge bg-primary me-2">
                                    {eliminationType === 'S' ? '싱글 엘리미네이션' : '더블 엘리미네이션'}
                                </span>
                                <span className="badge bg-secondary">
                                    참가자 {playerCount}명
                                </span>
                            </div>
                            <div className="d-flex gap-2">
                                <button className="btn btn-secondary btn-sm" onClick={handleGoHome}>
                                    <i className="bi bi-arrow-left me-2"></i>HOME
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 기본 헤더 (Home에서 오지 않은 경우만 표시) */}
                {!isFromHome && (
                    <div className="competition-header mb-3">
                        <h1 className="competition-title mb-0" style={{ color: theme === 'dark' ? '#e0e0e0' : 'inherit' }}>Sort Tournament Maker</h1>
                        <p className="mb-0 mt-2" style={{ color: theme === 'dark' ? '#a0a0a0' : '#6c757d' }}>토너먼트를 생성하고 대진표를 관리하세요</p>
                    </div>
                )}

                {/* 테마 선택 라디오 버튼 */}
                <div className="mb-3" style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: '8px',
                    border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease'
                }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: theme === 'dark' ? '#ffffff' : '#2d3748',
                        cursor: 'pointer',
                        fontWeight: theme === 'light' ? '600' : '400'
                    }}>
                        <input
                            type="radio"
                            name="theme"
                            value="light"
                            checked={theme === 'light'}
                            onChange={(e) => setTheme(e.target.value)}
                            style={{ cursor: 'pointer' }}
                        />
                        <i className="bi bi-sun-fill" style={{ fontSize: '1.1rem', color: '#ffd700' }}></i>
                        <span>라이트 테마</span>
                    </label>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: theme === 'dark' ? '#ffffff' : '#2d3748',
                        cursor: 'pointer',
                        fontWeight: theme === 'dark' ? '600' : '400'
                    }}>
                        <input
                            type="radio"
                            name="theme"
                            value="dark"
                            checked={theme === 'dark'}
                            onChange={(e) => setTheme(e.target.value)}
                            style={{ cursor: 'pointer' }}
                        />
                        <i className="bi bi-moon-stars-fill" style={{ fontSize: '1.1rem', color: '#667eea' }}></i>
                        <span>다크 테마</span>
                    </label>
                </div>

                {/* Step 1: 엘리미네이션 타입 선택 */}
                {step === 1 && (
                    <div className="row justify-content-center" style={{ paddingBottom: '3rem' }}>
                        <div className="col-12 col-md-10">
                            <h4 className="mb-4 text-center" style={{ color: theme === 'dark' ? '#e0e0e0' : 'inherit' }}>토너먼트 타입을 선택하세요</h4>
                            <div className="row g-4 mb-4">
                                <div className="col-md-6">
                                    <div
                                        className="card h-100 border-2 shadow-sm hover-lift"
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                                            border: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.2)' : '2px solid rgba(0, 0, 0, 0.125)'
                                        }}
                                        onClick={() => handleTypeSelect('S')}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#0d6efd';
                                            e.currentTarget.style.transform = 'translateY(-10px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(255, 215, 0, 0.2)' : 'rgba(0, 0, 0, 0.125)';
                                            e.currentTarget.style.transform = '';
                                        }}
                                    >
                                        <div className="card-body text-center p-3">
                                            <div style={{ fontSize: '2.8rem' }}>🏆</div>
                                            <h4 className="mt-2 mb-2" style={{ color: theme === 'dark' ? '#e0e0e0' : 'inherit' }}>싱글 엘리미네이션</h4>
                                            <p className="mb-0" style={{ fontSize: '0.9rem', color: theme === 'dark' ? '#a0a0a0' : '#6c757d' }}>
                                                한 번 패배하면 탈락하는 방식<br/>
                                                빠르고 간단한 토너먼트
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div
                                        className="card h-100 border-2 shadow-sm hover-lift"
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                                            border: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.2)' : '2px solid rgba(0, 0, 0, 0.125)'
                                        }}
                                        onClick={() => handleTypeSelect('D')}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#198754';
                                            e.currentTarget.style.transform = 'translateY(-10px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(255, 215, 0, 0.2)' : 'rgba(0, 0, 0, 0.125)';
                                            e.currentTarget.style.transform = '';
                                        }}
                                    >
                                        <div className="card-body text-center p-3">
                                            <div style={{ fontSize: '2.8rem' }}>⚔️</div>
                                            <h4 className="mt-2 mb-2" style={{ color: theme === 'dark' ? '#e0e0e0' : 'inherit' }}>더블 엘리미네이션</h4>
                                            <p className="mb-0" style={{ fontSize: '0.9rem', color: theme === 'dark' ? '#a0a0a0' : '#6c757d' }}>
                                                두 번 패배해야 탈락하는 방식<br/>
                                                패자부활전이 있는 토너먼트
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: 플레이어 입력 */}
                {step === 2 && (
                    <div className="row justify-content-center" style={{ paddingBottom: '2rem' }}>
                        <div className="col-12 col-md-10">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="mb-0" style={{ color: theme === 'dark' ? '#e0e0e0' : 'inherit' }}>Bracket을 선택하세요</h4>
                                <button className="btn btn-secondary btn-sm" onClick={() => {
                                    setStep(1);
                                    setPlayerCount(0);
                                    setPlayers([]);
                                }}>
                                    <i className="bi bi-arrow-left me-2"></i>이전
                                </button>
                            </div>

                            {playerCount === 0 ? (
                                <>
                                    <div className="card shadow-sm mb-3" style={{
                                        backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                                        border: theme === 'dark' ? '1px solid rgba(255, 215, 0, 0.2)' : '1px solid rgba(0, 0, 0, 0.125)'
                                    }}>
                                        <div className="card-body text-center p-3">
                                            <div className="d-flex gap-3 justify-content-center flex-wrap">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handlePlayerCountSelect(4)}
                                                    style={{ minWidth: '165px', padding: '0.735rem 1.47rem', fontSize: '1.76rem', fontWeight: 'bold' }}
                                                >
                                                    4강
                                                </button>
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handlePlayerCountSelect(8)}
                                                    style={{ minWidth: '165px', padding: '0.735rem 1.47rem', fontSize: '1.76rem', fontWeight: 'bold' }}
                                                >
                                                    8강
                                                </button>
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handlePlayerCountSelect(16)}
                                                    style={{ minWidth: '165px', padding: '0.735rem 1.47rem', fontSize: '1.76rem', fontWeight: 'bold' }}
                                                >
                                                    16강
                                                </button>
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handlePlayerCountSelect(32)}
                                                    style={{ minWidth: '165px', padding: '0.735rem 1.47rem', fontSize: '1.76rem', fontWeight: 'bold' }}
                                                >
                                                    32강
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 참자사 수 선택 안내 메시지 */}
                                    <div className="alert mb-1" style={{
                                        fontSize: '0.95rem',
                                        backgroundColor: theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#d1ecf1',
                                        border: theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.4)' : '1px solid #bee5eb',
                                        color: theme === 'dark' ? '#e0e0e0' : '#0c5460'
                                    }}>
                                        <i className="bi bi-info-circle me-2"></i>
                                        진행하려는 플레이어의 수보다 큰 Bracket을 선택하세요. *Ex: 6명 -&gt; 8강 선택(2명 부전승)
                                    </div>
                                    {/* 더블 엘리미네이션 안내 메시지 */}
                                    {eliminationType === 'D' && (
                                        <div className="alert mb-4" style={{
                                            fontSize: '0.95rem',
                                            backgroundColor: theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#d1ecf1',
                                            border: theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.4)' : '1px solid #bee5eb',
                                            color: theme === 'dark' ? '#e0e0e0' : '#0c5460'
                                        }}>
                                            <i className="bi bi-info-circle me-2"></i>
                                            듀얼엘리미네이션 특성상 공정한 게임 진행을 위하여 인원이 부족시 대진에 부전승을 적용하여 진행합니다.
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="card shadow-sm mb-4" style={{
                                        backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                                        border: theme === 'dark' ? '1px solid rgba(255, 215, 0, 0.2)' : '1px solid rgba(0, 0, 0, 0.125)'
                                    }}>
                                        <div className="card-header d-flex justify-content-between align-items-center" style={{
                                            backgroundColor: theme === 'dark' ? '#4a5568' : '#0d6efd',
                                            color: '#ffffff'
                                        }}>
                                            <h6 className="mb-0" style={{ fontSize: '1rem' }}>참가 {playerCount}명</h6>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-light"
                                                    onClick={handleAutoFill}
                                                    title="빈 칸을 자동으로 채웁니다"
                                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                                >
                                                    <i className="bi bi-magic me-1"></i>
                                                    <span style={{ fontSize: '0.75rem'}}>자동 생성</span>
                                                </button>
                                                <button
                                                    className="btn btn-warning"
                                                    onClick={handleShuffle}
                                                    title="플레이어 위치를 랜덤하게 섞습니다"
                                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                                >
                                                    <i className="bi bi-shuffle me-1"></i>
                                                    <span style={{ fontSize: '0.75rem'}}>랜덤 섞기</span>
                                                </button>
                                                <button
                                                    className="btn btn-danger"
                                                    onClick={handleClearAll}
                                                    title="모든 플레이어 이름을 지웁니다"
                                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                                >
                                                    <i className="bi bi-trash me-1"></i>
                                                    <span style={{ fontSize: '0.75rem'}}>지우기</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            <div className="alert mb-3" style={{
                                                backgroundColor: theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#d1ecf1',
                                                border: theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.4)' : '1px solid #bee5eb',
                                                color: theme === 'dark' ? '#e0e0e0' : '#0c5460'
                                            }}>
                                                <i className="bi bi-info-circle me-2"></i>
                                                참가자 이름을 모두 입력하지 않아도 토너먼트를 진행할 수 있습니다.
                                            </div>
                                            <div className="row g-3">
                                                {players.map((player, index) => (
                                                    <div key={index} className="col-md-6 col-lg-4">
                                                        <div className="input-group">
                                                            <span className="input-group-text" style={{
                                                                minWidth: '80px',
                                                                backgroundColor: theme === 'dark' ? '#4a5568' : '#e9ecef',
                                                                color: theme === 'dark' ? '#e0e0e0' : '#212529',
                                                                borderColor: theme === 'dark' ? 'rgba(255, 215, 0, 0.3)' : '#ced4da'
                                                            }}>
                                                                #{index + 1}
                                                            </span>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={player.name}
                                                                onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                                                                style={{
                                                                    backgroundColor: theme === 'dark' ? '#1a1a2e' : '#ffffff',
                                                                    color: theme === 'dark' ? '#e0e0e0' : '#212529',
                                                                    borderColor: theme === 'dark' ? 'rgba(255, 215, 0, 0.3)' : '#ced4da'
                                                                }}
                                                            />
                                                            {player.name && (
                                                                <button
                                                                    className="btn btn-outline-secondary"
                                                                    type="button"
                                                                    onClick={() => handlePlayerNameChange(index, '')}
                                                                    title="지우기"
                                                                    style={{
                                                                        padding: '0.375rem 0.75rem',
                                                                        borderLeft: 'none',
                                                                        backgroundColor: theme === 'dark' ? '#1a1a2e' : '#ffffff',
                                                                        color: theme === 'dark' ? '#e0e0e0' : '#6c757d',
                                                                        borderColor: theme === 'dark' ? 'rgba(255, 215, 0, 0.3)' : '#ced4da'
                                                                    }}
                                                                >
                                                                    <i className="bi bi-x-lg"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2 justify-content-center mt-4 mb-3">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setPlayerCount(0)}
                                            style={{ padding: '0.45rem 0.9rem', fontSize: '1rem' }}
                                        >
                                            참가자 수 재선택
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleGenerateTournament}
                                            style={{ padding: '0.45rem 0.9rem', fontSize: '1.08rem' }}
                                        >
                                            <i className="bi bi-play-circle me-2"></i>
                                            토너먼트 생성
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 3: 토너먼트 대진표 */}
                {step === 3 && (
                    <div style={{
                        paddingBottom: '4rem'
                    }}>
                        {/* Home에서 오지 않은 경우만 엘리미네이션 타입과 버튼 표시 */}
                        {!isFromHome && (<>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <span className="badge bg-primary me-1">
                                        {eliminationType === 'S' ? '싱글 엘리미네이션' : '더블 엘리미네이션'}
                                    </span>
                                    <span className="badge bg-secondary">
                                        참가자 {playerCount}명
                                    </span>
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-secondary btn-sm" onClick={() => setStep(2)}>
                                        <i className="bi bi-arrow-left me-2"></i>
                                        <span style={{ fontSize: '0.75rem'}}>이전</span>
                                    </button>
                                    <button className="btn btn-danger btn-sm" onClick={handleReset}>
                                        <i className="bi bi-arrow-counterclockwise me-2"></i>
                                        <span style={{ fontSize: '0.75rem'}}>처음부터 다시</span>
                                    </button>
                                </div>
                            </div>
                            </>
                        )}

                        <div className="row g-3">
                            {/* 왼쪽: 토너먼트 대진표 */}
                            <div className={isRankingVisible ? (isRankingExpanded ? "col-lg-9" : "col-lg-11") : "col-lg-12"}>
                                <div className="card shadow-sm" style={{
                                    backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                                    border: theme === 'dark' ? '1px solid rgba(255, 215, 0, 0.2)' : '1px solid rgba(0, 0, 0, 0.125)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div className="card-body p-2" style={{ overflowX: 'auto' }}>
                                        {eliminationType === 'S' ? renderSingleElimination() : renderDoubleElimination()}
                                    </div>
                                </div>

                                <div className="alert mt-3" style={{
                                    background: theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#d1ecf1',
                                    border: theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.4)' : '1px solid #bee5eb',
                                    color: theme === 'dark' ? '#e0e0e0' : '#0c5460',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <i className="bi bi-info-circle me-2"></i>
                                    선수를 클릭하면 승자로 선택됩니다. 승자는 언제든 변경할 수 있습니다.
                                </div>
                            </div>

                            {/* 우측: 최종 순위 */}
                            {isRankingVisible && (
                                <div className={isRankingExpanded ? "col-lg-3" : "col-lg-1"}>
                                    {renderRankings()}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* 토너먼트 안내 팝업 */}
            {showGuideModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                    onClick={() => setShowGuideModal(false)}
                >
                    <div
                        className="card shadow-lg"
                        style={{
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '80vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="card-header bg-primary text-white" style={{ flexShrink: 0 }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="bi bi-info-circle me-2"></i>
                                    토너먼트 진행 안내
                                </h5>
                                <button
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowGuideModal(false)}
                                    aria-label="Close"
                                    style={{ fontSize: '0.8rem' }}
                                ></button>
                            </div>
                        </div>
                        <div className="card-body p-4" style={{ overflowY: 'auto', flex: '1 1 auto' }}>
                            <div className="mb-4">
                                <h6 className="fw-bold mb-3">
                                    <i className="bi bi-1-circle-fill text-primary me-2"></i>
                                    시드 번호 순서대로 진행
                                </h6>
                                <p className="mb-2 ps-4">
                                    각 경기에는 <strong>시드 번호(#1, #2, #3...)</strong>가 부여되어 있습니다.
                                </p>
                                <p className="mb-0 ps-4">
                                    승자와 패자가 자동으로 다음 라운드로 이동합니다.
                                </p>
                            </div>

                            <div className="mb-4">
                                <h6 className="fw-bold mb-3">
                                    <i className="bi bi-2-circle-fill text-success me-2"></i>
                                    승자와 패자의 이동
                                </h6>
                                <div className="ps-4">
                                    <div className="d-flex align-items-start mb-2">
                                        <span className="badge bg-success me-2 mt-1">승자</span>
                                        <span>경기에서 이긴 선수는 <strong>승자조</strong>로 자동 이동합니다.</span>
                                    </div>
                                    <div className="d-flex align-items-start mb-2">
                                        <span className="badge bg-warning text-dark me-2 mt-1">패자</span>
                                        <span>경기에서 진 선수는 <strong>패자조</strong>로 이동하여 재도전 기회를 얻습니다.</span>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h6 className="fw-bold mb-3">
                                    <i className="bi bi-3-circle-fill text-info me-2"></i>
                                    승자 선택 방법
                                </h6>
                                <p className="mb-0 ps-4">
                                    대진표에서 <strong>선수 이름을 클릭</strong>하면 해당 선수가 승자로 선택되며, 자동으로 다음 라운드로 진출합니다.
                                </p>
                            </div>

                            <div className="alert alert-info mb-0">
                                <i className="bi bi-lightbulb me-2"></i>
                                <strong>Tip:</strong> 승자는 언제든 변경할 수 있으니 실수하셔도 걱정하지 마세요!
                            </div>
                        </div>
                        <div className="card-footer bg-light" style={{ flexShrink: 0 }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="dontShowTodayCheck"
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const today = new Date().toDateString();
                                                sessionStorage.setItem('tournamentGuide_dontShowToday', today);
                                            } else {
                                                sessionStorage.removeItem('tournamentGuide_dontShowToday');
                                            }
                                        }}
                                    />
                                    <label className="form-check-label" htmlFor="dontShowTodayCheck">
                                        오늘 그만보기
                                    </label>
                                </div>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowGuideModal(false)}
                                    style={{ minWidth: '120px' }}
                                >
                                    <i className="bi bi-check-lg me-2"></i>
                                    확인
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TrmntMaker;
